/**
 * MSAL Instance
 * 
 * Creates and exports the PublicClientApplication instance
 * for use throughout the application.
 */

import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, isEntraConfigured } from "./authConfig";

/**
 * Initialize MSAL instance only if Entra is configured
 * This handles the OAuth 2.0 Authorization Code Flow with PKCE
 */
export const msalInstance = isEntraConfigured()
    ? new PublicClientApplication(msalConfig)
    : null;

/**
 * Initialize MSAL and handle redirect promise
 * This must be called before any other MSAL operations
 */
export const initializeMsal = async () => {
    // Skip initialization if Entra is not configured
    if (!msalInstance) {
        console.log('[MSAL] Entra External ID not configured, skipping MSAL initialization');
        return;
    }

    try {
        if (typeof msalInstance.initialize === 'function') {
            await msalInstance.initialize();
        }
        // Handle redirect promise (returns from Entra External ID)
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
            console.log('[MSAL] Redirect response received:', {
                account: response.account?.username,
                scopes: response.scopes,
            });

            // Set active account
            msalInstance.setActiveAccount(response.account);
        } else {
            // No redirect response, check if there are existing accounts
            const accounts = msalInstance.getAllAccounts();

            if (accounts.length > 0) {
                // Set the first account as active
                msalInstance.setActiveAccount(accounts[0]);
                console.log('[MSAL] Active account set:', accounts[0].username);
            }
        }
    } catch (error) {
        console.error('[MSAL] Error handling redirect promise:', error);
    }
};

/**
 * Add event callbacks for MSAL
 * Useful for logging and debugging authentication flow
 */
if (msalInstance) {
    msalInstance.addEventCallback((event) => {
        // Log authentication events in development
        if (process.env.NODE_ENV === 'development') {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                console.log('[MSAL Event] Login success:', event.payload);

                if (event.payload?.account) {
                    msalInstance.setActiveAccount(event.payload.account);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                console.error('[MSAL Event] Login failure:', event.error);
            }

            if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                console.log('[MSAL Event] Token acquired:', {
                    scopes: event.payload?.scopes,
                    expiresOn: event.payload?.expiresOn,
                });
            }

            if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                console.error('[MSAL Event] Token acquisition failure:', event.error);
            }

            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                console.log('[MSAL Event] Logout success');
            }
        }
    });
}

/**
 * Get the currently active account
 */
export const getActiveAccount = () => {
    return msalInstance?.getActiveAccount() || null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    if (!msalInstance) return false;
    const account = msalInstance.getActiveAccount();
    return account !== null;
};

export default msalInstance;
